import { ImagePlus } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";

interface ComposerAddCardButtonProps {
  onAdd: (files: File[]) => void;
  /**
   * `compact` renders a toolbar button (used when cards already exist); the
   * default renders the full drag-and-drop zone for the empty state.
   */
  compact?: boolean;
}

/**
 * Multi-image picker. Every dropped/selected image becomes a staged card
 * (validated in the store). Both variants accept drag-and-drop.
 */
export const ComposerAddCardButton: React.FC<ComposerAddCardButtonProps> = ({
  onAdd,
  compact = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = useCallback(
    (list: FileList | null) => {
      if (!list || list.length === 0) return;
      onAdd(Array.from(list));
      if (inputRef.current) inputRef.current.value = "";
    },
    [onAdd],
  );

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      multiple
      onChange={(e) => handleFiles(e.target.files)}
      className="sr-only"
    />
  );

  const dragProps = {
    onDragOver: (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setDragOver(true);
    },
    onDragLeave: () => setDragOver(false),
    onDrop: (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
  };

  if (compact) {
    return (
      <label
        {...dragProps}
        className={[
          "inline-flex cursor-pointer items-center gap-2 rounded-2xl border-2 bg-white/10 px-5 py-3 text-sm font-extrabold text-white shadow-[0_4px_0_rgba(0,0,0,0.3)] backdrop-blur-sm transition-all active:translate-y-0.5 active:shadow-[0_2px_0_rgba(0,0,0,0.3)]",
          dragOver
            ? "border-[#14B8A6] bg-white/15"
            : "border-white/15 hover:border-[#14B8A6] hover:bg-white/15",
        ].join(" ")}
      >
        {input}
        <ImagePlus className="h-5 w-5 shrink-0" aria-hidden />
        Add cards
      </label>
    );
  }

  return (
    <label
      {...dragProps}
      className={[
        "group flex min-h-[200px] cursor-pointer items-center justify-center gap-3 rounded-2xl border-4 border-dashed bg-white/[0.04] px-6 py-10 text-center backdrop-blur-sm transition-colors",
        dragOver
          ? "border-[#14B8A6] bg-[#14B8A6]/10"
          : "border-white/25 hover:border-[#14B8A6]",
      ].join(" ")}
    >
      {input}
      <span
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#ECFDF5] text-[#0F766E] shadow-[0_4px_0_#A7F3D0]"
        aria-hidden
      >
        <ImagePlus className="h-6 w-6" />
      </span>
      <span className="flex flex-col items-start">
        <span className="text-base font-extrabold text-white">
          Drop images — each becomes a card
        </span>
        <span className="text-sm font-bold text-white/60">
          PNG or JPG · 2 MB each · inherits the defaults above
        </span>
      </span>
    </label>
  );
};
